// src/app/app.component.ts (Final, Standalone, and SSR-Safe Version)

import { Component, OnInit, ChangeDetectorRef, AfterViewInit, Inject, PLATFORM_ID, OnDestroy } from "@angular/core";
import { isPlatformBrowser, CommonModule } from "@angular/common";
import { Router, ActivatedRoute, NavigationStart, NavigationEnd, Event, NavigationCancel, NavigationError, RouterOutlet } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { Observable, fromEvent } from 'rxjs';
import { mergeMap, filter, map } from "rxjs/operators";

import { LoadingService } from "./services/loading.service";
import { SeoService } from "./services/seo.service";
import { JsonLdService } from "./services/json-ld.service";
import { WowService } from "./services/wow.service";
import { ToasterContainerComponent } from "./toaster-container.component";

import { environment } from '../environments/environment';
import { constant } from "./constants/constants";
import { CanonicalService } from "./canonical.service";

@Component({
  selector: "app-root",
  standalone: true, // <-- Now a true standalone component
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  imports: [CommonModule, ToasterContainerComponent, RouterOutlet],
})
export class AppComponent implements OnInit, AfterViewInit {
  title = "KudosHub";
  loading: boolean;
  showOfferPopup = false;
  popupComponentLoaded = false;
  popupComponent: any;
  gtag: Function;

  constructor(
    private router: Router,
    private loadingService: LoadingService,
    private titleService: Title,
    private _seoService: SeoService,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private jsonLdService: JsonLdService,
    private canonicalService: CanonicalService,
    private wow: WowService, // Preserved from your original file
    @Inject(PLATFORM_ID) private platformId: Object // Injected for SSR safety
  ) {
    // This router logic is safe for both server and browser
    this.router.events.subscribe((routerEvent: Event) => {
      if (routerEvent instanceof NavigationStart) {
        this.loadingService.setLoading(true);
        this.loadingService.loading$.subscribe((res: boolean) => {
          this.loading = res;
        });
      }
      if (
        routerEvent instanceof NavigationEnd ||
        routerEvent instanceof NavigationCancel ||
        routerEvent instanceof NavigationError
      ) {
        this.loadingService.setLoading(false);
        this.cdr.detectChanges();
        this.loadingService.loading$.subscribe((res: boolean) => {
          this.loading = res;
        });
      }
    });
  }

  ngOnInit() {
    // Initialize canonical link
    this.canonicalService.initCanonical();

    // JSON-LD dynamic schema setup
    this.jsonLdService.setupRouteListener();

    // Default title (fallback)
    this.titleService.setTitle("KudosHub Technologies | Home");

    // Route-based logic (SEO + Popup)
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),

        // Get deepest activated route
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),

        filter(route => route.outlet === "primary"),

        // Extract route data
        mergeMap(route => route.data)
      )
      .subscribe(data => {

        /* -----------------------------------------
         * 1. OFFER POPUP LOGIC (CLEAN + SCALABLE)
         * -----------------------------------------*/
        if (isPlatformBrowser(this.platformId)) {
          const disablePopup = data['disablePopup'] === true;

          if (!disablePopup) {
            if (sessionStorage.getItem("popState") !== "showed") {
              setTimeout(() => {
                this.loadPopupComponent();
                this.showOfferPopup = true;
                sessionStorage.setItem("popState", "showed");
              }, constant.offerPopupTime);
            }
          }
        }

        /* -----------------------------------------
         * 2. SEO META UPDATES
         * -----------------------------------------*/
        if (data["title"]) this.titleService.setTitle(data["title"]);
        if (data["description"]) this._seoService.updateDescription(data["description"]);
        if (data["keywords"]) this._seoService.updateKeywords(data["keywords"]);
        if (data["ogTitle"]) this._seoService.updateOgTitle(data["ogTitle"]);
        if (data["ogDesc"]) this._seoService.updateOgDesc(data["ogDesc"]);
        if (data["ogImage"]) this._seoService.updateOgImg(data["ogImage"]);
        if (data["ogUrl"]) this._seoService.updateOgUrl(data["ogUrl"]);
        if (data["twitterTitle"]) this._seoService.updateTwitterTitle(data["twitterTitle"]);
        if (data["twitterDesc"]) this._seoService.updateTwitterDesc(data["twitterDesc"]);
        if (data["twitterImage"]) this._seoService.updateTwitterImg(data["twitterImage"]);
        if (data["dateModified"]) this._seoService.updateDate(data["dateModified"]);
      });
  }


  ngAfterViewInit() {
    // --- THIS IS THE CRITICAL FIX ---
    // We wrap ALL browser-only logic in this check.
    if (isPlatformBrowser(this.platformId)) {
      // 1. Initialize WOW.js animations
      this.wow.init();

      // 2. Handle the offer popup logic using sessionStorage
      if (sessionStorage.getItem("popState") != "showed") {
        setTimeout(() => {
          this.loadPopupComponent();
          this.showOfferPopup = true;
          sessionStorage.setItem("popState", "showed");
        }, constant.offerPopupTime);
      }

      // 3. Dynamically load the external index.js script
      setTimeout(() => {
        let script1 = document.createElement('script');
        document.body.appendChild(script1);
        fromEvent(script1, 'load').subscribe(() => this.onScriptLoad());
        script1.src = environment.KUDOSHUB_SITE_URL + "theme/js/index.js";
      }, 10);

      // 4. Set up Google Analytics page tracking
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          // Check if gtag function is available on the window object
          if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('config', 'G-PT7WRK0H24', {
              page_path: event.urlAfterRedirects
            });

            if (event.urlAfterRedirects.includes('/verify-account')) {
              (window as any).gtag('event', 'conversion', {
                send_to: 'AW-728910286/SRlQCO2IwIwYEM6TydsC'
              });
            }
          }
        });
    }
  }

  loadPopupComponent() {
    import('./offer-popup/offer-popup.component')
      .then(mod => {
        this.popupComponent = mod.OfferPopupComponent;
        this.popupComponentLoaded = true;
      })
      .catch(error => {
        console.error('Error loading popup component:', error);
      });
  }

  onScriptLoad() { }

  getShowOfferPopupValue($event: boolean) {
    this.showOfferPopup = $event;
  }
}